import React, { useState, useEffect } from "react";
import { Link, Navigate, useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Eye, EyeOff } from "lucide-react";
import { logIn, getSession } from "../../../Slices/sessionSlice";
import { newAlert } from "../../../Slices/alertSlice";
import "./SignIn.css";

// Google logo SVG as a data URL
const GoogleLogo = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%234285F4' d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'/%3E%3Cpath fill='%2334A853' d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'/%3E%3Cpath fill='%23FBBC05' d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'/%3E%3Cpath fill='%23EA4335' d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'/%3E%3C/svg%3E";

// Get backend URL from environment variables
const backendBaseUrl = process.env.REACT_APP_BACK_END_DOMAIN || "";

const SignIn = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    sessionUser,
  } = useSelector((state) => state.sessionSlice);

  const [formValues, setFormValues] = useState({
    email: "",
    password: "",
  });

  const [formErrors, setFormErrors] = useState({
    email: "",
    password: "",
  });

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleSignInError, setGoogleSignInError] = useState("");
  const [googleEmail, setGoogleEmail] = useState("");

  // Add effect to populate email from URL query parameter
  useEffect(() => {
    const emailFromUrl = searchParams.get("email");
    if (emailFromUrl) {
      setFormValues((prev) => ({
        ...prev,
        email: emailFromUrl,
      }));
    }
  }, [searchParams]);

  // Handle URL error parameters
  useEffect(() => {
    const error = searchParams.get("error");
    if (error) {
      // Handle "Account does not exist" error specifically
      if (error === "Account does not exist") {
        const email = searchParams.get("email") || "";
        setGoogleEmail(email);
        setGoogleSignInError(error);
        dispatch(newAlert({ type: "danger", message: error }));
      } else {
        dispatch(newAlert({ type: "danger", message: error }));
      }
      // Remove the error parameter from URL after processing
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("error");
      newSearchParams.delete("email");
      navigate({ search: newSearchParams.toString() }, { replace: true });
    }
  }, [searchParams, dispatch, navigate]);

  // Note: Server errors are handled in handleFormSubmit with generic message
  // No alerts are shown for server errors

  // Update form field values
  const setFormField = (fieldName, value) => {
    setFormValues({
      ...formValues,
      [fieldName]: value,
    });
    // Clear error when user starts typing
    if (formErrors[fieldName]) {
      setFormErrors({
        ...formErrors,
        [fieldName]: "",
      });
    }
  };

  // Handle input field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormField(name, value);
  };

  // Toggle password visibility
  const handlePasswordVisibleToggle = () => {
    setPasswordVisible((previous) => !previous);
  };

  // Validate form fields and return any validation errors
  const validateForm = (values) => {
    const errors = {};
    const { email, password } = values;

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const isValidEmail = emailRegex.test(email);

    if (!email) {
      errors.email = "Please enter your email";
    } else if (!isValidEmail) {
      errors.email = "Please enter a valid email";
    }

    if (!password) {
      errors.password = "Please enter your password";
    }

    return errors;
  };

  // Handle form submission and trigger sign-in
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setFormErrors({ email: "", password: "" });

    const errors = validateForm(formValues);
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }

    setLoading(true);
    try {
      const result = await dispatch(logIn(formValues)).unwrap();
      
      if (result.isLoggedIn && result.sessionUser) {
        // Successfully logged in - getSession will be called automatically
        // or we can dispatch it to refresh the session
        dispatch(getSession());
      }
    } catch (error) {
      // Extract error details from the rejected value
      const errorMessage = error?.error?.message || error?.message || "An error occurred";
      const statusCode = error?.statusCode;

      // Handle different error types based on status code
      if (statusCode === 400) {
        // Validation errors (400) - show in appropriate form fields
        const lowerMessage = errorMessage.toLowerCase();
        if (lowerMessage.includes("email")) {
          // Email-related errors
          if (lowerMessage.includes("required")) {
            setFormErrors({
              email: "Please enter your email",
              password: "",
            });
          } else if (lowerMessage.includes("invalid") || lowerMessage.includes("format")) {
            setFormErrors({
              email: "Please enter a valid email",
              password: "",
            });
          } else {
            setFormErrors({
              email: errorMessage,
              password: "",
            });
          }
        } else if (lowerMessage.includes("password")) {
          // Password-related errors
          setFormErrors({
            email: "",
            password: errorMessage,
          });
        } else if (lowerMessage.includes("required")) {
          // Both fields required
          setFormErrors({
            email: "Email and password are required",
            password: "Email and password are required",
          });
        } else {
          // Generic validation error
          setFormErrors({
            email: errorMessage,
            password: "",
          });
        }
      } else if (statusCode === 401) {
        // Authentication errors (401) - show on password field and alert
        // These are "Email or password is incorrect" type errors
        setFormErrors({
          email: "",
          password: errorMessage,
        });
        dispatch(newAlert({ type: "danger", message: errorMessage }));
      } else if (statusCode >= 500) {
        // Server errors (500+) - show generic error message, no alert
        const genericErrorMessage = "Something went wrong. Please try again later.";
        setFormErrors({
          email: "",
          password: genericErrorMessage,
        });
      } else {
        // Other errors (network, etc.) - show in form
        setFormErrors({
          email: "",
          password: errorMessage,
        });
        dispatch(newAlert({ type: "danger", message: errorMessage }));
      }
    } finally {
      setLoading(false);
    }
  };

  // Redirect to appropriate page after successful sign-in
  if (sessionUser && sessionUser.roles && sessionUser.roles.length > 0) {
    const redirectTo = searchParams.get("ref") || "/";
    
    // Redirect based on user role
    if (sessionUser.roles.includes("super-admin") || sessionUser.roles.includes("admin")) {
      return <Navigate to="/admin/createUser" replace />;
    } else if (sessionUser.roles.includes("OrgAdmin")) {
      return <Navigate to="/org-admin/dashboard" replace />;
    } else if (sessionUser.roles.includes("Supervisor")) {
      return <Navigate to="/supervisor/dashboard" replace />;
    } else if (sessionUser.roles.includes("Candidate")) {
      return <Navigate to="/candidateDashboard" replace />;
    }
    
    return <Navigate to={redirectTo} replace />;
  }

  // Render the sign-in form
  return (
    <div className="signin-container">
      <div className="signin-card">
        {/* Form header */}
        {/* <i className="fas fa-bolt text-primary signin-icon" /> */}
        <h2 className="signin-title">Sign In</h2>

        {/* Google sign-in section */}
        {/* <div className="signin-google-section">
          <a
            className="btn btn-google-signin"
            href={`${backendBaseUrl}/api/session/login/google?redirectTo=/&action=si`}
          >
            <img
              src={GoogleLogo}
              className="google-logo"
              alt="google logo"
            />
            <span>Sign in with Google</span>
          </a>
        </div>

        {googleSignInError === "Account does not exist" && (
          <div className="signin-google-error">
            <p>
              Account does not exist for {googleEmail}. Do you want to sign up?{" "}
              <Link to="/sign-up" className="signin-link">
                Sign up
              </Link>
            </p>
          </div>
        )} */}

        {/* Email sign-in section */}
        {/* <div className="signin-divider">
          <span>or Sign In with email</span>
        </div> */}

        <form onSubmit={handleFormSubmit} noValidate>
          {/* Email input field */}
          <div className="mb-3">
            <label htmlFor="email" className="form-label">
              Enter your email
            </label>
            <input
              type="email"
              className={`form-control ${
                formErrors.email ? "is-invalid" : ""
              }`}
              id="email"
              name="email"
              value={formValues.email}
              onChange={handleInputChange}
              autoComplete="email"
            />
            {formErrors.email && (
              <div className="invalid-feedback d-block">{formErrors.email}</div>
            )}
          </div>

          {/* Password input field */}
          <div className="mb-3">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <div className="password-input-wrapper">
              <input
                type={passwordVisible ? "text" : "password"}
                className={`form-control ${
                  formErrors.password ? "is-invalid" : ""
                }`}
                id="password"
                name="password"
                value={formValues.password}
                onChange={handleInputChange}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={handlePasswordVisibleToggle}
              >
                {passwordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {formErrors.password && (
              <div className="invalid-feedback d-block">
                {formErrors.password}
              </div>
            )}
            <div className="signin-password-links">
              <Link
                to="/forgot-password"
                className="signin-link"
              >
                Forgot password?
              </Link>
              {/* <Link
                to="/get-login-link-to-your-email"
                className="signin-link"
              >
                Get login link to your email
              </Link> */}
            </div>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            className="btn btn-primary btn-signin w-100 mb-3"
            disabled={loading}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Signing In...
              </>
            ) : (
              "Sign In"
            )}
          </button>

          {/* Sign up link */}
          <p className="text-center signin-signup-text">
            Don't have an account?{" "}
            <span
              className="signin-link signin-link-bold"
              onClick={() => {
                navigate("/sign-up");
              }}
              style={{ cursor: "pointer" }}
            >
              Sign Up
            </span>
          </p>
        </form>
      </div>
    </div>
  );
};

export default SignIn;

